import { useEffect, useState } from 'react';
import { api } from '@/shared/api/client';
import { applyTheme, metricsOf, type Theme } from '@/shared/lib/theme';
import { demoTheme } from './demo';

interface PartnerState {
  name: string;
  theme: Theme;
  /** Настройки не доехали: работаем на теме по умолчанию. */
  isDefault: boolean;
}

const fallback = (slug: string): PartnerState => ({
  name: '',
  theme: demoTheme(slug),
  isDefault: true,
});

/** Ответ живёт в памяти вкладки: тема меняется раз в полгода. */
const cache = new Map<string, PartnerState>();

/**
 * Настройки партнёра: имя и оформление.
 *
 * Тема применяется до первой отрисовки содержимого, иначе человек успевает
 * увидеть чужие цвета и смену — на светлой теме из тёмной это выглядит
 * как вспышка. Пока настройки едут, виджет держит заглушку.
 */
export function usePartner(slug: string): PartnerState & { isLoading: boolean } {
  const cached = cache.get(slug);
  const [state, setState] = useState<PartnerState | null>(cached ?? null);

  useEffect(() => {
    if (cache.has(slug)) return;

    let alive = true;
    api
      .partnerConfig(slug)
      .then((config) => ({ name: config.name, theme: config.theme, isDefault: false }))
      .catch(() => fallback(slug))
      .then((next) => {
        cache.set(slug, next);
        if (alive) setState(next);
      });

    return () => {
      alive = false;
    };
  }, [slug]);

  // Тему кладём на корневой узел, а не в React: переменные должны быть
  // видны и тем частям вёрстки, что лежат вне дерева компонентов.
  //
  // И на сам документ тоже. Страница за модулем красится значением
  // по умолчанию — тёмным, от первого заказчика, — и у светлой темы
  // оно вылезало чёрными углами за скруглением оболочки. Снаружи это
  // читается как рамка непонятного происхождения.
  useEffect(() => {
    if (!state) return;

    const root = document.getElementById('ls-root');
    if (root) applyTheme(root, state.theme);

    applyTheme(document.documentElement, state.theme);
  }, [state]);

  return { ...(state ?? fallback(slug)), isLoading: state === null };
}

/** Ширина макета партнёра — нужна масштабу, поэтому вынесена отдельно. */
export function partnerMetrics(slug: string) {
  return metricsOf(cache.get(slug)?.theme ?? {});
}
